// src/components/LabelPrinter.jsx - FINAL: 40x30 and 30x20 now share the same logic.
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import QRCode from 'qrcode';
import { formatUSD, toIntOr } from '../utils/formatters'; // Using local formatPriceForTag

const mmToPt = (mm) => (mm / 25.4) * 72;
const inToPt = (inch) => inch * 72;

// --- LOCAL FORMATTER ---
// Include a dollar sign for clearer price readability on tags.
const formatPriceForTag = (price) => {
  if (price === null || price === undefined) return '';
  const num = toIntOr(price, 0);
  if (num === 0) return '';
  return `$${num}`;
};
// Digits-only formatter (used so we can render a smaller $ without shrinking the numerals)
const formatPriceDigitsForTag = (price) => {
  if (price === null || price === undefined) return '';
  const num = toIntOr(price, 0);
  if (num === 0) return '';
  return String(num);
};

// Draw a price with a smaller, subtly raised $ that won't overpower the numerals.
// This prevents confusion between prices and inventory/SKU-like numbers, while keeping a clean hierarchy.
const drawPriceWithSmallDollar = (page, digitsText, opts) => {
  const {
    font,
    size,
    minSize = 4,
    maxWidth,
    x,
    y,
    align = 'center', // 'center' | 'right' | 'left'
    // Slightly larger currency mark, tighter spacing so it reads as one price token.
    dollarScale = 0.72,
    gap = 0.25,
    color = rgb(0, 0, 0),
  } = opts;

  const digits = String(digitsText || '').trim();
  if (!digits) return;

  let digitSize = size;

  // If adding the currency symbol would overflow, shrink *only as needed* to fit.
  const measureTotalWidth = (dSize) => {
    const dSizeClamped = Math.max(minSize, dSize);
    const dollarSize = Math.max(5, Math.round(dSizeClamped * dollarScale));
    const dollarWidth = font.widthOfTextAtSize('$', dollarSize);
    const digitsWidth = font.widthOfTextAtSize(digits, dSizeClamped);
    return { dSize: dSizeClamped, dollarSize, totalWidth: dollarWidth + gap + digitsWidth, dollarWidth };
  };

  let m = measureTotalWidth(digitSize);
  while (typeof maxWidth === 'number' && maxWidth > 0 && m.totalWidth > maxWidth && digitSize > minSize) {
    digitSize -= 1;
    m = measureTotalWidth(digitSize);
  }

  // Compute startX based on alignment.
  let startX = x;
  if (align === 'right') {
    // x is the right edge
    startX = x - m.totalWidth;
  } else if (align === 'center') {
    // x is the left edge of the box
    startX = x + (maxWidth - m.totalWidth) / 2;
  } // 'left' uses x as-is

  // Raise the $ slightly so it reads as a currency marker, not a leading digit.
  const raise = Math.max(0, Math.round((m.dSize - m.dollarSize) * 0.45));

  page.drawText('$', {
    x: startX,
    y: y + raise,
    font,
    size: m.dollarSize,
    color,
  });

  page.drawText(digits, {
    x: startX + m.dollarWidth + gap,
    y,
    font,
    size: m.dSize,
    color,
  });
};
// --- END LOCAL FORMATTER ---

const AVERY_5167_BASE = {
  kind: 'sheet',
  w: inToPt(1.75),
  h: inToPt(0.5),
  cols: 4,
  rows: 20,
  margins: { top: inToPt(0.5), bottom: inToPt(0.5), left: inToPt(0.3), right: inToPt(0.3) },
  gapX: inToPt(0.3),
  gapY: inToPt(0.0),
};


const AVERY_5160_BASE = {
  kind: 'sheet',
  w: inToPt(2.625),
  h: inToPt(1.0),
  cols: 3,
  rows: 10,
  margins: { top: inToPt(0.5), bottom: inToPt(0.5), left: inToPt(0.1875), right: inToPt(0.1875) },
  gapX: inToPt(0.125),
  gapY: inToPt(0.0),
};

const AVERY_94102_BASE = {
  kind: 'sheet',
  w: inToPt(0.75),   // 3/4" width
  h: inToPt(0.75),   // 3/4" height
  cols: 8,           // 8 columns x 10 rows = 80 labels
  rows: 10,
  margins: {
    top: inToPt(0.625),    // 5/8" top margin
    bottom: inToPt(0.625), // 5/8" bottom margin
    left: inToPt(0.375),   // 3/8" left margin
    right: inToPt(0.375),  // 3/8" right margin
  },
  gapX: inToPt(0.25), // 1/4" horizontal gap between labels
  gapY: inToPt(0.25), // 1/4" vertical gap between labels
};

// --- PLS275 BASE (Premium Label Supply 1" x 0.5", 8 x 20 = 160 per sheet) ---
const PLS275_BASE = {
  kind: 'sheet',
  w: inToPt(1.0),   // 1" width
  h: inToPt(0.5),   // 0.5" height
  cols: 8,          // 8 columns x 20 rows = 160 labels per sheet
  rows: 20,
  margins: {
    top: inToPt(0.5),    // 0.5" top margin
    bottom: inToPt(0.5), // 0.5" bottom margin
    left: inToPt(0.25),  // 0.25" left margin
    right: inToPt(0.25), // 0.25" right margin
  },
  gapX: inToPt(0.0), // no horizontal gap between labels
  gapY: inToPt(0.0), // no vertical gap between labels
};

const PRESET_REGISTRY = {
  PHOMEMO_40x30: { kind: 'thermal', w: mmToPt(40), h: mmToPt(30), name: 'Phomemo_40x30mm' },
  PHOMEMO_30x20: { kind: 'thermal', w: mmToPt(30), h: mmToPt(20), name: 'Phomemo_30x20mm' },
  BROTHER_CSV: { kind: 'csv', name: 'Brother_CSV', csvSchema: 'v1' },
  DYMO_CSV: { kind: 'csv', name: 'DYMO_CSV', csvSchema: 'v1' },
  ZEBRA_CSV: { kind: 'csv', name: 'Zebra_CSV', csvSchema: 'v1' },
  ROLLO_CSV: { kind: 'csv', name: 'Rollo_CSV', csvSchema: 'v1' },
  AVERY_5160_QR: { ...AVERY_5160_BASE, name: 'Avery 5160 / 8160 (QR)', qrEnabled: true },
  AVERY_5167: { ...AVERY_5167_BASE, name: 'Avery 5167 / 8167' },
  AVERY_5160: { ...AVERY_5160_BASE, name: 'Avery 5160 / 8160' },
  AVERY_94102: { ...AVERY_94102_BASE, name: 'Avery 94102 (3/4" Square)' },
  PLS275: { ...PLS275_BASE, name: 'PLS275 1" x 0.5" (Premium Label Supply)' },
};

// --- THIS FUNCTION NOW CENTERS EACH LINE OF TEXT ---
const drawWrappedText = (page, text, options) => {
  const { font, size, lineHeight, maxWidth, startX, startY, endY, color = rgb(0, 0, 0) } = options;
  const words = String(text || '').trim().split(/\s+/).filter(w => w.length > 0);
  let currentLine = '';
  let currentY = startY;
  for (const word of words) {
    const testLine = currentLine === '' ? word : `${currentLine} ${word}`;
    const testWidth = font.widthOfTextAtSize(testLine, size);
    if (testWidth > maxWidth && currentLine !== '') {
      const lineWidth = font.widthOfTextAtSize(currentLine, size);
      const centeredX = startX + (maxWidth - lineWidth) / 2;
      page.drawText(currentLine, { x: centeredX, y: currentY, font, size, color });
      currentY -= size * lineHeight;
      currentLine = word;
      // Truncation bug was here. It is gone.
    } else {
      currentLine = testLine;
    }
  }
  if (currentY >= endY && currentLine !== '') {
    const lineWidth = font.widthOfTextAtSize(currentLine, size);
    const centeredX = startX + (maxWidth - lineWidth) / 2;
    page.drawText(currentLine, { x: centeredX, y: currentY, font, size, color });
  }
};

const calculateWrappedTextHeight = (text, font, size, lineHeight, maxWidth) => {
  const words = String(text || '').trim().split(/\s+/).filter(w => w.length > 0);
  if (!words.length) return 0;
  let lineCount = 1;
  let currentLine = '';
  for (const word of words) {
    const testLine = currentLine === '' ? word : `${currentLine} ${word}`;
    const testWidth = font.widthOfTextAtSize(testLine, size);
    if (testWidth > maxWidth && currentLine !== '') {
      lineCount++;
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  // Return the actual height including the line spacing
  return (lineCount - 1) * size * lineHeight + size; 
};

// --- THIS IS THE ROBUST, VERTICALLY-CENTERING FUNCTION ---
const drawTextInBox = (page, text, options) => {
  const { font, maxSize, minSize, lineHeight, maxWidth, startX, startY, endY, color = rgb(0, 0, 0) } = options;
  const availableHeight = startY - endY;
  
  let finalSize = minSize; // Default to min size

  // Find the largest font size that fits
  for (let size = maxSize; size >= minSize; size--) {
    const requiredHeight = calculateWrappedTextHeight(text, font, size, lineHeight, maxWidth);
    if (requiredHeight <= availableHeight) {
      finalSize = size;
      break; // Found the best size
    }
  }
  
  // Vertically center the *entire text block*
  const requiredHeight = calculateWrappedTextHeight(text, font, finalSize, lineHeight, maxWidth);
  const verticalPadding = (availableHeight - requiredHeight) / 2;
  const correctedStartY = (startY - verticalPadding) - finalSize;
  
  drawWrappedText(page, text, {
    font,
    size: finalSize,
    lineHeight,
    maxWidth,
    startX,
    startY: correctedStartY,
    endY: correctedStartY - requiredHeight, // Use exact height
    color,
  });

  return requiredHeight; // Return the height it actually used
};
// --- END OF FUNCTION ---

// --- BEGIN: Mixed-width title helpers ---
// First line can use the full label width; wrapped lines use a narrower left column
const layoutMixedLines = (text, font, size, firstLineWidth, otherLinesWidth) => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  if (!words.length) return lines;

  let currentLine = '';
  for (const word of words) {
    const limit = lines.length === 0 ? firstLineWidth : otherLinesWidth;
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    const w = font.widthOfTextAtSize(candidate, size);
    if (w > limit && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = candidate;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
};

const drawMixedTitleBlock = (page, text, options) => {
  const {
    font,
    maxSize,
    minSize,
    lineHeight,
    firstLineWidth,
    otherLinesWidth,
    startXFull,
    startXLeft,
    startY,
    endY,
    color = rgb(0, 0, 0),
  } = options;

  const availableHeight = startY - endY;
  if (availableHeight <= 0) return 0;

  let chosenSize = minSize;
  let lines = [];

  for (let size = maxSize; size >= minSize; size--) {
    const candidateLines = layoutMixedLines(text, font, size, firstLineWidth, otherLinesWidth);
    if (!candidateLines.length) {
      lines = [];
      chosenSize = size;
      break;
    }
    const lineGap = size * lineHeight;
    const blockHeight = (candidateLines.length - 1) * lineGap + size;
    if (blockHeight <= availableHeight) {
      lines = candidateLines;
      chosenSize = size;
      break;
    }
  }

  if (!lines.length) return 0;

  const lineGap = chosenSize * lineHeight;
  const blockHeight = (lines.length - 1) * lineGap + chosenSize;
  const verticalPadding = (availableHeight - blockHeight) / 2;
  let y = (startY - verticalPadding) - chosenSize;

  for (let i = 0; i < lines.length; i++) {
    const limit = i === 0 ? firstLineWidth : otherLinesWidth;
    const baseX = i === 0 ? startXFull : startXLeft;
    const line = lines[i];
    const lineWidth = font.widthOfTextAtSize(line, chosenSize);
    const x = baseX + (limit - lineWidth) / 2;

    if (y >= endY) {
      page.drawText(line, { x, y, font, size: chosenSize, color });
    }
    y -= lineGap;
  }

  return blockHeight;
};
// --- END: Mixed-width title helpers ---

// --- BEGIN: Limited-line centered text helpers ---
const layoutWrappedLines = (text, font, size, maxWidth) => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  if (!words.length) return lines;

  let currentLine = '';
  for (const word of words) {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    const w = font.widthOfTextAtSize(candidate, size);
    if (w > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = candidate;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
};

const calculateWrappedTextHeightLimited = (text, font, size, lineHeight, maxWidth, maxLines) => {
  const lines = layoutWrappedLines(text, font, size, maxWidth).slice(0, maxLines);
  if (!lines.length) return 0;
  return (lines.length - 1) * size * lineHeight + size;
};

const drawTextInBoxLimitedLines = (page, text, options) => {
  const {
    font,
    maxSize,
    minSize,
    lineHeight,
    maxWidth,
    startX,
    startY,
    endY,
    maxLines,
    color = rgb(0, 0, 0),
  } = options;

  const availableHeight = startY - endY;
  if (availableHeight <= 0) return 0;

  let chosenSize = minSize;
  let lines = [];

  // First pass: pick the largest size where up to maxLines fit in the band.
  for (let size = maxSize; size >= minSize; size--) {
    const allLines = layoutWrappedLines(text, font, size, maxWidth);
    if (!allLines.length) continue;

    const candidateLines = allLines.slice(0, maxLines);
    const blockHeight = (candidateLines.length - 1) * size * lineHeight + size;

    if (blockHeight <= availableHeight) {
      chosenSize = size;
      lines = candidateLines;
      break;
    }
  }

  // Fallback: if nothing fit at any size, we still want to render *something*.
  // Use minSize and drop trailing lines until the block fits vertically.
  if (!lines.length) {
    const allLinesAtMin = layoutWrappedLines(text, font, minSize, maxWidth);
    if (!allLinesAtMin.length) return 0;

    let candidateLines = allLinesAtMin.slice(0, maxLines);

    // Reduce the number of lines one by one until the block fits
    while (candidateLines.length > 1) {
      const blockHeightCandidate =
        (candidateLines.length - 1) * minSize * lineHeight + minSize;
      if (blockHeightCandidate <= availableHeight) break;
      candidateLines = candidateLines.slice(0, candidateLines.length - 1);
    }

    // Final check: if even a single line at minSize can't fit, obey the law and draw nothing.
    const finalBlockHeight =
      (candidateLines.length - 1) * minSize * lineHeight + minSize;
    if (finalBlockHeight > availableHeight) {
      return 0;
    }

    chosenSize = minSize;
    lines = candidateLines;
  }

  const blockHeight = (lines.length - 1) * chosenSize * lineHeight + chosenSize;
  const verticalPadding = (availableHeight - blockHeight) / 2;
  let y = (startY - verticalPadding) - chosenSize;

  for (const line of lines) {
    const lineWidth = font.widthOfTextAtSize(line, chosenSize);
    const x = startX + (maxWidth - lineWidth) / 2;
    if (y >= endY) {
      page.drawText(line, { x, y, font, size: chosenSize, color });
    }
    y -= chosenSize * lineHeight;
  }

  return blockHeight;
};
// --- END: Limited-line centered text helpers ---

const calculatePriceFontSize = (text, font, maxSize, minSize, maxWidth) => {
  let size = maxSize;
  while (size > minSize) {
    if (font.widthOfTextAtSize(text, size) <= maxWidth) return size;
    size -= 1;
  }
  return minSize;
};

const TITLE_RULES_DEFAULT = {
  hyphenEnabled: true,
  minWordLen: 10,
  minLeft: 3,
  minRight: 3,
  maxLines: 3,
};

const TITLE_RULES_BY_PRESET = {
  AVERY_5160: { maxLines: 3 },
  AVERY_5167: { maxLines: 2 },
  AVERY_94102: { maxLines: 4 },
  PLS275: { maxLines: 2 },
  PHOMEMO_30x20: { maxLines: 3 },
  PHOMEMO_40x30: { maxLines: 4 },
};

const getTitleRules = (presetKey) => ({
  ...TITLE_RULES_DEFAULT,
  ...(TITLE_RULES_BY_PRESET[presetKey] || {}),
});

const findHyphenSplit = (word, font, size, maxWidth, rules) => {
  if (!rules.hyphenEnabled) return null;
  if (word.length < rules.minWordLen) return null;
  const minLeft = rules.minLeft;
  const minRight = rules.minRight;
  const maxLeft = word.length - minRight;
  for (let split = maxLeft; split >= minLeft; split -= 1) {
    const left = word.slice(0, split);
    const right = word.slice(split);
    if (left.length < minLeft || right.length < minRight) continue;
    const leftWithHyphen = `${left}-`;
    if (font.widthOfTextAtSize(leftWithHyphen, size) <= maxWidth) {
      return { left: leftWithHyphen, right };
    }
  }
  return null;
};

const truncateToWidth = (text, font, size, maxWidth) => {
  const raw = String(text || '');
  if (!raw) return '';
  let lo = 0;
  let hi = raw.length;
  let best = '';
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const candidate = raw.slice(0, mid);
    const w = font.widthOfTextAtSize(candidate, size);
    if (w <= maxWidth) {
      best = candidate;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best || raw.slice(0, 1);
};

const applyAvery5160TitleCap = (title) => {
  const raw = String(title || '');
  if (raw.length <= 100) return raw;
  const wsIndex = raw.lastIndexOf(' ', 100);
  if (wsIndex >= 0) {
    return raw.slice(0, wsIndex).trimEnd();
  }
  return raw.slice(0, 100);
};

const applyAvery94102TitleCap = (title) => {
  const raw = String(title || '');
  if (raw.length <= 30) return raw;
  const wsIndex = raw.lastIndexOf(' ', 30);
  if (wsIndex >= 0) {
    return raw.slice(0, wsIndex).trimEnd();
  }
  return raw.slice(0, 30);
};

const layoutTitleLines = (text, font, size, maxWidth, maxLines, rules, allowTruncation) => {
  const raw = String(text || '').trim();
  if (!raw) return [];
  const words = raw.split(/\s+/).filter(Boolean);
  const isSingleWord = words.length === 1;

  if (isSingleWord) {
    const word = words[0];
    if (font.widthOfTextAtSize(word, size) <= maxWidth) {
      return [word];
    }

    const lines = [];
    let remaining = word;
    while (lines.length < maxLines - 1) {
      const split = findHyphenSplit(remaining, font, size, maxWidth, rules);
      if (!split) break;
      lines.push(split.left);
      remaining = split.right;
      if (font.widthOfTextAtSize(remaining, size) <= maxWidth) {
        lines.push(remaining);
        return lines;
      }
    }

    if (font.widthOfTextAtSize(remaining, size) <= maxWidth) {
      lines.push(remaining);
      return lines;
    }

    if (!allowTruncation) return [];
    const truncated = truncateToWidth(remaining, font, size, maxWidth);
    lines.push(truncated);
    return lines.slice(0, maxLines);
  }

  const lines = [];
  let currentLine = '';
  let wordIndex = 0;

  while (wordIndex < words.length) {
    const word = words[wordIndex];
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      currentLine = candidate;
      wordIndex += 1;
      continue;
    }

    if (currentLine) {
      lines.push(currentLine);
      currentLine = '';
      if (lines.length >= maxLines) {
        return allowTruncation ? lines.slice(0, maxLines) : [];
      }
      continue;
    }

    const split = findHyphenSplit(word, font, size, maxWidth, rules);
    if (split) {
      lines.push(split.left);
      if (lines.length >= maxLines) {
        return allowTruncation ? lines.slice(0, maxLines) : [];
      }
      words[wordIndex] = split.right;
      continue;
    }

    if (!allowTruncation) return [];
    return lines;
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  if (lines.length > maxLines) {
    return allowTruncation ? lines.slice(0, maxLines) : [];
  }

  return lines;
};

const computeTitleLayout = (text, font, maxSize, minSize, lineHeight, maxWidth, maxLines, rules, availableHeight) => {
  if (availableHeight <= 0) return null;

  let chosenSize = minSize;
  let lines = [];

  for (let size = maxSize; size >= minSize; size--) {
    const candidateLines = layoutTitleLines(text, font, size, maxWidth, maxLines, rules, false);
    if (!candidateLines.length) continue;
    const blockHeight = (candidateLines.length - 1) * size * lineHeight + size;
    if (blockHeight <= availableHeight) {
      chosenSize = size;
      lines = candidateLines;
      break;
    }
  }

  if (!lines.length) {
    const fallbackLines = layoutTitleLines(text, font, minSize, maxWidth, maxLines, rules, true);
    if (!fallbackLines.length) return null;
    let candidateLines = fallbackLines;
    while (candidateLines.length > 1) {
      const blockHeightCandidate =
        (candidateLines.length - 1) * minSize * lineHeight + minSize;
      if (blockHeightCandidate <= availableHeight) break;
      candidateLines = candidateLines.slice(0, candidateLines.length - 1);
    }
    const finalBlockHeight =
      (candidateLines.length - 1) * minSize * lineHeight + minSize;
    if (finalBlockHeight > availableHeight) return null;
    chosenSize = minSize;
    lines = candidateLines;
  }

  const blockHeight = (lines.length - 1) * chosenSize * lineHeight + chosenSize;
  return { size: chosenSize, lines, blockHeight };
};

const drawTitleInBand = (page, text, options) => {
  const {
    font,
    maxSize,
    minSize,
    lineHeight,
    maxWidth,
    startX,
    startY,
    endY,
    maxLines,
    rules,
    color = rgb(0, 0, 0),
  } = options;

  const availableHeight = startY - endY;
  const layout = computeTitleLayout(text, font, maxSize, minSize, lineHeight, maxWidth, maxLines, rules, availableHeight);
  if (!layout) return 0;
  const { size: chosenSize, lines, blockHeight } = layout;
  const verticalPadding = (availableHeight - blockHeight) / 2;
  let y = (startY - verticalPadding) - chosenSize;

  for (const line of lines) {
    const lineWidth = font.widthOfTextAtSize(line, chosenSize);
    const x = startX + (maxWidth - lineWidth) / 2;
    if (y >= endY) {
      page.drawText(line, { x, y, font, size: chosenSize, color });
    }
    y -= chosenSize * lineHeight;
  }

  return blockHeight;
};

// This helper function is used by the *original* 5167 logic, so it must stay.
const drawSingleLineEllipsis = (page, text, opts) => {
  const { font, size, maxWidth, x, y, color = rgb(0, 0, 0) } = opts;
  let t = String(text || '').trim();
  if (!t) return;

  let w = font.widthOfTextAtSize(t, size);
  if (w <= maxWidth) {
    const centeredX = x + (maxWidth - w) / 2;
    page.drawText(t, { x: centeredX, y, font, size, color });
    return;
  }

  const ell = '…';
  let lo = 0,
    hi = t.length;
  let best = '';
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const candidate = t.slice(0, mid).trim() + ell;
    w = font.widthOfTextAtSize(candidate, size);
    if (w <= maxWidth) {
      best = candidate;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  
  w = font.widthOfTextAtSize(best || ell, size);
  const centeredX = x + (maxWidth - w) / 2;
  page.drawText(best || ell, { x: centeredX, y, font, size, color });
};


const generateThermalPdf = async (preset, items, businessNameParam, headingTextParam) => {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const businessName = businessNameParam || '';
  const headingText = (headingTextParam || '').trim();
  const is40x30 = Math.abs(preset.w - mmToPt(40)) < 0.5 && Math.abs(preset.h - mmToPt(30)) < 0.5;
  const is30x20 = Math.abs(preset.w - mmToPt(30)) < 0.5 && Math.abs(preset.h - mmToPt(20)) < 0.5;

  for (const item of items) {
    const quantity = toIntOr(item.quantity_estimate, 1);
    for (let q = 0; q < quantity; q++) {
      const page = pdfDoc.addPage([preset.w, preset.h]);
      const isSpacer = !!item?.isSpacer;
      const businessNameForItem = isSpacer ? '' : businessName;
      const priceText = isSpacer ? '' : (formatPriceForTag(item.per_item_cost_estimate) || '');
      const rawTitleText = (item.title || '').trim();
      const headingSize = is30x20 ? 6 : is40x30 ? 7 : 7;
      const headingGap = headingText ? 2 : 0;
      const headingBlock = headingText ? headingSize + headingGap : 0;
      if (headingText) {
        page.drawText(headingText, {
          x: 2,
          y: preset.h - headingSize - 1,
          font: boldFont,
          size: headingSize,
          color: rgb(0.15, 0.15, 0.15),
        });
      }
      // --- MODIFICATION: 40x30 and 30x20 now use the SAME logic ---
      if (is40x30 || is30x20) {
        // --- "Price-First Authority" Layout (for 30x20 AND 40x30) ---
        const margin = is30x20 ? 4 : 1; // extra padding for 30x20 to avoid edge hits
        const contentTop = preset.h - headingBlock;
        const contentPadX = is40x30 ? 2 : 0;
        const fullInnerWidth = preset.w - margin * 2 - contentPadX * 2;
        const contentLeft = margin + contentPadX;
        // Title text: hard-cap 30x20 and 40x30 based on explicit calibration phrases
        // to avoid trying to render full paragraphs on tiny thermal labels.
        // 30x20 cap: "Tarrega Ft-110 Classical Guitar – Made In Sweden"
        // 40x30 cap: "Tarrega Ft-110 Classical Guitar – Made In Sweden (distributed By Fender). Quality"
        const maxChars40x30 = 'Tarrega Ft-110 Classical Guitar – Made In Sweden (distributed By Fender). Quality'.length;

        const titleText = is30x20
          ? rawTitleText
          : is40x30
          ? rawTitleText
          : rawTitleText;

        // --- DEFINE ZONES ---
        const nameTop = contentTop - margin; // Pushed below heading
        const priceBottom = margin + 2;    // Lift price band slightly off bottom margin
        const yInnerTop = contentTop - margin;
        const yInnerBottom = margin;

        const nameFraction = is30x20 ? 0.18 : 0.15;
        const priceFraction = is30x20 ? 0.30 : 0.25;

        const priceAvailableHeight = preset.h * priceFraction;
        const nameAvailableHeight = preset.h * nameFraction;

        const priceAreaTop = priceBottom + priceAvailableHeight;
        const nameAreaBottom = nameTop - nameAvailableHeight;

        const titleGap = 1; // 1pt gap
        let titleAreaTop = nameAreaBottom - titleGap;
        let titleAreaBottom = priceAreaTop + titleGap;

        if (is30x20) {
          const NAME_TOP_PADDING_PH30x20 = 0.0;
          const NAME_BOTTOM_GAP_PH30x20 = 0.5;
          const PRICE_BOTTOM_PADDING_PH30x20 = 0.0;
          const PRICE_TOP_GAP_PH30x20 = 0.5;

          const nameMaxSize = 7;
          const nameMinSize = 5;
          const nameSize = businessNameForItem
            ? calculatePriceFontSize(businessNameForItem, font, nameMaxSize, nameMinSize, fullInnerWidth)
            : 0;
          const nameHeight = businessNameForItem ? font.heightAtSize(nameSize) : 0;

          const priceMaxFontSize = 14;
          const priceMinFontSize = 8;
          let priceSize = calculatePriceFontSize(
            priceText,
            font,
            priceMaxFontSize,
            priceMinFontSize,
            fullInnerWidth
          );
          let priceHeight = font.heightAtSize(priceSize);

          const nameTopAnchor = yInnerTop - NAME_TOP_PADDING_PH30x20;
          const nameBottom = nameTopAnchor - nameHeight;

          const priceBottomAnchor = yInnerBottom + PRICE_BOTTOM_PADDING_PH30x20;
          const priceTop = priceBottomAnchor + priceHeight;

          titleAreaTop = nameBottom - NAME_BOTTOM_GAP_PH30x20;
          titleAreaBottom = priceTop + PRICE_TOP_GAP_PH30x20;
        }

        // 1. Company Name (TOP, Centered, Shrinks/Wraps)
        const nameMaxSize = is30x20 ? 7 : 10; // 40x30 gets +1pt
        const nameMinSize = is30x20 ? 5 : 7;

        if (businessNameForItem) {
          if (is30x20) {
            const nameSize = calculatePriceFontSize(businessNameForItem, font, nameMaxSize, nameMinSize, fullInnerWidth);
            const nameHeight = font.heightAtSize(nameSize);
            const nameLineWidth = font.widthOfTextAtSize(businessNameForItem, nameSize);
            const nameX = contentLeft + (fullInnerWidth - nameLineWidth) / 2;
            const nameTopAnchor = yInnerTop - 0.0;
            page.drawText(businessNameForItem, {
              x: nameX,
              y: nameTopAnchor - nameHeight,
              font,
              size: nameSize,
              color: rgb(0.3, 0.3, 0.3),
            });
          } else {
            drawTextInBox(page, businessNameForItem, {
              font,
              maxSize: nameMaxSize,
              minSize: nameMinSize,
              lineHeight: 1.1,
              maxWidth: fullInnerWidth,
              startX: contentLeft, // drawTextInBox will center
              startY: nameTop,
              endY: nameAreaBottom,
              color: rgb(0.3, 0.3, 0.3),
            });
          }
        }

        // 2. Price (BOTTOM, Hero, Bigger)
        // Use regular weight on 30x20 so the thermal print doesn't feel over-inked now that the title is regular.
        // Preserve hierarchy by allowing a slightly larger max size on 30x20.
        const priceMaxFontSize = is30x20 ? 14 : 18;
        const priceMinFontSize = is30x20 ? 8 : 8;
        const priceFont = font;

        let priceSize = calculatePriceFontSize(
          priceText,
          priceFont,
          priceMaxFontSize,
          priceMinFontSize,
          fullInnerWidth
        );

        let priceHeight = priceFont.heightAtSize(priceSize);
        if (!is30x20) {
          while (priceHeight > priceAvailableHeight && priceSize > priceMinFontSize) {
            priceSize -= 1;
            priceHeight = priceFont.heightAtSize(priceSize);
          }
        }

        // Center for 30x20, lower for 40x30 so it feels more intentional.
        const basePriceY = priceBottom + (priceAvailableHeight - priceHeight) / 2;
        const priceY = is30x20
          ? yInnerBottom + 0.0
          : priceBottom + (priceAvailableHeight - priceHeight) * 0.25;

        if (priceText) {
          const priceWidth = priceFont.widthOfTextAtSize(priceText, priceSize);
          const priceX = contentLeft + (fullInnerWidth - priceWidth) / 2;
          page.drawText(priceText, {
            x: priceX,
            y: priceY,
            font: priceFont,
            size: priceSize,
            color: rgb(0, 0, 0),
          });
        }

        // 3. Title (MIDDLE, Bigger, dynamic by length and label size)
        const titleLen = titleText.length;
        let titleMaxSize;
        let titleMinSize;

        if (is30x20) {
          // Tiny 30x20: short titles should be bold, longer ones step down.
          // With the taller title band, we can push each bucket 1pt larger.
          if (titleLen <= 18) {
            titleMaxSize = 19;
          } else if (titleLen <= 32) {
            titleMaxSize = 17;
          } else {
            titleMaxSize = 15;
          }
          titleMinSize = 8; // don't go micro-small on 30x20
        } else {
          // 40x30: larger canvas; same step-down idea as 30x20, but allows more characters
          if (titleLen <= 32) {
            titleMaxSize = 22;
          } else if (titleLen <= 64) {
            titleMaxSize = 20;
          } else {
            titleMaxSize = 18;
          }
          titleMinSize = 10;
        }

        // Choose the font for the title: for 30x20, use regular Helvetica; for 40x30, use bold.
        const titleFont = is30x20 ? font : boldFont;

        // For 40x30 only, enforce that even the longest single word fits inside the label
        // at the chosen maximum title size. If a word is too wide, we shrink the allowed
        // max size until every word fits horizontally. This prevents any characters from
        // running off the left/right edges (law: no text outside the label).
        if (is40x30) {
          const words40 = String(titleText || '').trim().split(/\s+/).filter(Boolean);
          let safeMaxSize40 = titleMaxSize;
          for (const word of words40) {
            while (
              safeMaxSize40 > titleMinSize &&
              titleFont.widthOfTextAtSize(word, safeMaxSize40) > fullInnerWidth
            ) {
              safeMaxSize40 -= 1;
            }
          }
          titleMaxSize = Math.max(titleMinSize, safeMaxSize40);
        }

        const titleRules = getTitleRules(is30x20 ? 'PHOMEMO_30x20' : 'PHOMEMO_40x30');
        drawTitleInBand(page, titleText, {
          font: titleFont,
          maxSize: titleMaxSize,
          minSize: titleMinSize,
          lineHeight: 1.1,
          maxWidth: fullInnerWidth,
          startX: contentLeft,
          startY: titleAreaTop,
          endY: titleAreaBottom,
          maxLines: titleRules.maxLines,
          rules: titleRules,
        });
      } else {
        // --- Original Generic Thermal Logic (UNCHANGED except price rendering) ---
        const margin = 5;
        const contentTop = preset.h - headingBlock;
        const titleSize = preset.h > 70 ? 12 : 10;
        const maxPriceSize = preset.h > 70 ? 36 : 28;
        const minPriceSize = 10;
        const nameSize = preset.h > 70 ? 8 : 4;
        const lineHeight = 1.2;
        const safeGap = 5;
        const nameMaxWidth = preset.w / 2 - margin;
        if (businessNameForItem) {
          page.drawText(businessNameForItem, { x: margin, y: margin, font, size: nameSize, color: rgb(0.2, 0.2, 0.2) });
        }
        const priceZoneWidth = preset.w - nameMaxWidth - margin * 2 - safeGap;
        const dynamicPriceSize = calculatePriceFontSize(priceText, boldFont, maxPriceSize, minPriceSize, priceZoneWidth);
        const priceHeight = boldFont.heightAtSize(dynamicPriceSize);
        const priceY = margin;
        const titleRules = getTitleRules('DEFAULT');
        drawTitleInBand(page, rawTitleText, {
          font,
          maxSize: titleSize,
          minSize: titleSize,
          lineHeight,
          maxWidth: preset.w - margin * 2,
          startX: margin,
          startY: contentTop - margin,
          endY: priceY + priceHeight,
          maxLines: titleRules.maxLines,
          rules: titleRules,
        });
        if (priceText) {
          const priceWidth = boldFont.widthOfTextAtSize(priceText, dynamicPriceSize);
          const priceX = preset.w - margin - priceWidth;
          page.drawText(priceText, {
            x: priceX,
            y: priceY,
            font: boldFont,
            size: dynamicPriceSize,
            color: rgb(0, 0, 0),
          });
        }
      }
    }
  }
  return pdfDoc;
};

const generateAverySheetPdf = async (preset, items, footerTextParam, headingTextParam, startAtParam) => {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const footerText = (footerTextParam || '').trim();
  const headingText = (headingTextParam || '').trim();
  const appOrigin = (process.env.REACT_APP_APP_ORIGIN || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  const qrCache = new Map();
  const QR_SIZE_PT = 44;
  const QR_PAD_PT = 2;
  const QR_GUTTER_PT = 3;

  const dataUrlToBytes = (dataUrl) => {
    const base64 = String(dataUrl || '').split(',')[1] || '';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  };

  const getQrImage = async (token) => {
    if (qrCache.has(token)) return qrCache.get(token);
    const targetUrl = `${appOrigin}/i/${token}`;
    const dataUrl = await QRCode.toDataURL(targetUrl, {
      errorCorrectionLevel: 'M',
      margin: 0,
      width: Math.round(QR_SIZE_PT),
    });
    const pngBytes = dataUrlToBytes(dataUrl);
    const pngImage = await pdfDoc.embedPng(pngBytes);
    qrCache.set(token, pngImage);
    return pngImage;
  };

  // This is the drawSingleLineEllipsis from your file, which we need for the 5167 logic
  const drawSingleLineEllipsis = (page, text, opts) => {
    const { font, size, maxWidth, x, y, color = rgb(0, 0, 0) } = opts;
    let t = String(text || '').trim();
    if (!t) return;
    
    let w = font.widthOfTextAtSize(t, size);
    if (w <= maxWidth) {
      const centeredX = x + (maxWidth - w) / 2;
      page.drawText(t, { x: centeredX, y, font, size, color });
      return;
    }
    const ell = '…';
    let lo = 0,
      hi = t.length;
    let best = '';
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      const candidate = t.slice(0, mid).trim() + ell;
      w = font.widthOfTextAtSize(candidate, size);
      if (w <= maxWidth) {
        best = candidate;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    w = font.widthOfTextAtSize(best || ell, size);
    const centeredX = x + (maxWidth - w) / 2;
    page.drawText(best || ell, { x: centeredX, y, font, size, color });
  };

  const pageW = inToPt(8.5),
    pageH = inToPt(11);
  const m = preset.margins || { top: inToPt(0.5), bottom: inToPt(0.5), left: inToPt(0.5), right: inToPt(0.5) };
  const cols = preset.cols || 3;
  const rows = preset.rows || 10;
  const labelsPerPage = cols * rows;
  const xGap = typeof preset.gapX === 'number' ? preset.gapX : 0;
  const yGap = typeof preset.gapY === 'number' ? preset.gapY : 0;

  const headingSize = 12;
  const drawPageHeading = (targetPage) => {
    if (!headingText) return;
    const maxWidth = pageW - m.left - m.right;
    const y = pageH - m.top * 0.6;
    drawSingleLineEllipsis(targetPage, headingText, {
      font: boldFont,
      size: headingSize,
      maxWidth,
      x: m.left,
      y,
      color: rgb(0.15, 0.15, 0.15),
    });
  };

  const startAtRaw = typeof startAtParam === 'number' ? startAtParam : parseInt(startAtParam, 10);
  const startAt = Number.isFinite(startAtRaw) && startAtRaw > 0 ? startAtRaw : 1;
  const startIndex = Math.max(0, Math.min(startAt - 1, labelsPerPage - 1));

  let page = pdfDoc.addPage([pageW, pageH]);
  drawPageHeading(page);
  let count = startIndex;

  for (const item of items) {
    const quantity = toIntOr(item?.quantity_estimate, 1);
    for (let q = 0; q < quantity; q++) {
      if (count >= labelsPerPage) {
        page = pdfDoc.addPage([pageW, pageH]);
        drawPageHeading(page);
        count = 0;
      }
      const row = Math.floor(count / cols);
      const col = count % cols;

      const x = m.left + col * (preset.w + xGap);
      const y = pageH - m.top - row * (preset.h + yGap);

      const isTiny = preset.h <= inToPt(0.5) + 0.01;
      const is5160 = !isTiny && Math.abs(preset.h - inToPt(1.0)) < 0.01 && Math.abs(preset.w - inToPt(2.625)) < 0.01;
      const is94102 = !isTiny && Math.abs(preset.h - inToPt(0.75)) < 0.01 && Math.abs(preset.w - inToPt(0.75)) < 0.01;
      const isPLS275 =
        !is5160 &&
        !is94102 &&
        Math.abs(preset.h - inToPt(0.5)) < 0.01 &&
        Math.abs(preset.w - inToPt(1.0)) < 0.01 &&
        cols === 8 &&
        rows === 20;

      // --- Sheet padding ---
      const useCenteredSheetPadding = is5160 || is94102 || isPLS275;
      const padY = useCenteredSheetPadding ? 4 : (isTiny ? 2 : 3);
      const padX = padY;
      const innerLeft = x + padX;
      const innerRight = x + preset.w - padX;
      const innerTop = y - padY;
      const innerBottom = y - preset.h + padY;
      const lawGap = isTiny ? 2 : 0; // Tighter gap
      const fullInnerWidth = Math.max(0, innerRight - innerLeft);
      const isSpacer = !!item?.isSpacer;
      const footerTextForItem = isSpacer ? '' : footerText;
      const rawTitleText = (item.title || '').trim();
      const priceText = isSpacer ? '' : (formatPriceForTag(item.per_item_cost_estimate) || '');
      const BOTTOM_MARGIN_PT_BY_PRESET = {
        AVERY_5160: 3,
        AVERY_94102: 3,
        PLS275: 3,
        DEFAULT: 2,
      };
      const presetKeyName = is5160 ? 'AVERY_5160' : is94102 ? 'AVERY_94102' : isPLS275 ? 'PLS275' : 'DEFAULT';
      const bottomMarginReq = BOTTOM_MARGIN_PT_BY_PRESET[presetKeyName] || 3;
      const BOTTOM_BIAS_PT = 1;

      if (is5160) {
        // --- 5160: Centered stack (business name, title, price) ---
        const innerHeight = innerTop - innerBottom;
        const qrEnabled = !!preset.qrEnabled;
        const qrToken = qrEnabled && !isSpacer ? (item.qr_token || item.qrToken) : null;
        if (qrEnabled && !isSpacer && !qrToken) {
          throw new Error('QR labels require qr_token on every item. Refresh inventory and try again.');
        }
        const qrColWidth = qrEnabled ? (QR_SIZE_PT + (QR_PAD_PT * 2)) : 0;
        const textMaxWidth = Math.max(0, fullInnerWidth - (qrEnabled ? (qrColWidth + QR_GUTTER_PT) : 0));
        const textLeft = innerLeft;

        const gapNameTitle = 1;
        const gapTitlePrice = 1;

        const nameMaxSize = 10;
        const nameMinSize = 8;
        const nameSize = footerTextForItem
          ? calculatePriceFontSize(footerTextForItem, font, nameMaxSize, nameMinSize, textMaxWidth)
          : 0;
        const nameHeight = footerTextForItem ? font.heightAtSize(nameSize) : 0;

        const priceMax = 13;
        const priceMin = 9;
        const priceSize = calculatePriceFontSize(priceText, boldFont, priceMax, priceMin, textMaxWidth);
        const priceHeight = priceText ? boldFont.heightAtSize(priceSize) : 0;

        const titleText = applyAvery5160TitleCap(rawTitleText);
        let titleMaxSize = 18;
        const titleLen = titleText.length;
        if (titleLen > 24) titleMaxSize = 16;
        if (titleLen > 48) titleMaxSize = 14;
        const titleMinSize = 6;
        const titleLineHeight = 1.02;
        const titleRules = getTitleRules('AVERY_5160');

        const maxTitleAvailableHeight = Math.max(
          0,
          innerHeight -
            nameHeight -
            (nameHeight > 0 ? gapNameTitle : 0) -
            priceHeight -
            (priceHeight > 0 ? gapTitlePrice : 0)
        );
        const titleLayout = computeTitleLayout(
          titleText,
          boldFont,
          titleMaxSize,
          titleMinSize,
          titleLineHeight,
          textMaxWidth,
          titleRules.maxLines,
          titleRules,
          maxTitleAvailableHeight
        );
        const titleBlockHeight = titleLayout ? titleLayout.blockHeight : 0;

        const totalBlockHeight =
          nameHeight +
          (nameHeight > 0 ? gapNameTitle : 0) +
          titleBlockHeight +
          (priceHeight > 0 ? gapTitlePrice : 0) +
          priceHeight;

        const centerY = innerBottom + innerHeight / 2;
        let blockTopY = centerY + totalBlockHeight / 2 + BOTTOM_BIAS_PT;
        let blockBottomY = blockTopY - totalBlockHeight;
        const minBlockBottomY = innerBottom + bottomMarginReq;
        if (blockBottomY < minBlockBottomY) {
          const delta = minBlockBottomY - blockBottomY;
          blockTopY += delta;
          blockBottomY += delta;
        }

        let cursorY = blockTopY;

        if (qrEnabled) {
          const qrOuterRight = innerRight;
          const qrOuterTop = innerTop;
          const qrOuterX = qrOuterRight - qrColWidth;
          const qrOuterY = qrOuterTop - qrColWidth;
          page.drawRectangle({
            x: qrOuterX,
            y: qrOuterY,
            width: qrColWidth,
            height: qrColWidth,
            color: rgb(1, 1, 1),
          });
          if (!isSpacer && qrToken) {
            const qrImage = await getQrImage(qrToken);
            page.drawImage(qrImage, {
              x: qrOuterX + QR_PAD_PT,
              y: qrOuterY + QR_PAD_PT,
              width: QR_SIZE_PT,
              height: QR_SIZE_PT,
            });
          }
        }

        if (footerTextForItem && nameHeight > 0) {
          const nameBaselineY = cursorY - nameHeight;
          const nameLineWidth = font.widthOfTextAtSize(footerTextForItem, nameSize);
          const nameX = textLeft + (textMaxWidth - nameLineWidth) / 2;
          page.drawText(footerTextForItem, {
            x: nameX,
            y: nameBaselineY,
            font,
            size: nameSize,
            color: rgb(0.3, 0.3, 0.3),
          });
          cursorY = nameBaselineY - (nameHeight > 0 ? gapNameTitle : 0);
        }

        if (titleBlockHeight > 0) {
          const titleTopY = cursorY;
          const titleBottomY = titleTopY - titleBlockHeight;
          drawTitleInBand(page, titleText, {
            font: boldFont,
            maxSize: titleMaxSize,
            minSize: titleMinSize,
            lineHeight: titleLineHeight,
            maxWidth: textMaxWidth,
            startX: textLeft,
            startY: titleTopY,
            endY: titleBottomY,
            maxLines: titleRules.maxLines,
            rules: titleRules,
          });
          cursorY = titleBottomY - (priceHeight > 0 ? gapTitlePrice : 0);
        }

        if (priceText && priceHeight > 0) {
          const priceBaselineY = cursorY - priceHeight;
          const labelBottomY = y - preset.h;
          const minPriceBaselineY = labelBottomY + bottomMarginReq + priceHeight * 0.1;
          const finalPriceBaselineY = Math.max(priceBaselineY, minPriceBaselineY);
          const priceWidth = boldFont.widthOfTextAtSize(priceText, priceSize);
          const priceX = textLeft + (textMaxWidth - priceWidth) / 2;
          page.drawText(priceText, {
            x: priceX,
            y: finalPriceBaselineY,
            font: boldFont,
            size: priceSize,
            color: rgb(0, 0, 0),
          });
        }

      } else if (isPLS275) {
        // --- PLS275: Centered stack (title, price only) ---
        const innerHeight = innerTop - innerBottom;

        const gapTitlePrice = 0;

        const rawTitleTextTinyPls = rawTitleText;
        const maxCharsTinyPls = 'Tarrega Ft-110 Classical Guitar'.length;
        const titleText = rawTitleTextTinyPls.slice(0, maxCharsTinyPls);

        let titleMaxSizeTinyPls = 9;
        const titleLenTinyPls = titleText.length;
        if (titleLenTinyPls > 24) titleMaxSizeTinyPls = 8;
        if (titleLenTinyPls > 40) titleMaxSizeTinyPls = 7;
        const titleMinSizeTinyPls = 7;
        const titleLineHeightTinyPls = 1.0;

        const priceMaxTinyPls = 11;
        const priceMinTinyPls = 6;
        const priceSizeTinyPls = calculatePriceFontSize(priceText, boldFont, priceMaxTinyPls, priceMinTinyPls, fullInnerWidth);
        const priceHeightTinyPls = priceText ? boldFont.heightAtSize(priceSizeTinyPls) : 0;

        const titleRules = getTitleRules('PLS275');
        const maxTitleAvailableHeight = Math.max(
          0,
          innerHeight - priceHeightTinyPls - (priceHeightTinyPls > 0 ? gapTitlePrice : 0)
        );
        let titleLayout = computeTitleLayout(
          titleText,
          boldFont,
          titleMaxSizeTinyPls,
          titleMinSizeTinyPls,
          titleLineHeightTinyPls,
          fullInnerWidth,
          titleRules.maxLines,
          titleRules,
          maxTitleAvailableHeight
        );

        if (titleLayout && titleLayout.lines.length === 1) {
          const candidateSize = titleLayout.size + 1;
          const candidateLines = layoutTitleLines(
            titleText,
            boldFont,
            candidateSize,
            fullInnerWidth,
            titleRules.maxLines,
            titleRules,
            false
          );
          if (candidateLines.length > 0 && candidateLines.length <= titleRules.maxLines) {
            const candidateHeight =
              (candidateLines.length - 1) * candidateSize * titleLineHeightTinyPls + candidateSize;
            if (candidateHeight <= maxTitleAvailableHeight) {
              titleLayout = { size: candidateSize, lines: candidateLines, blockHeight: candidateHeight };
            }
          }
        }

        const titleBlockHeight = titleLayout ? titleLayout.blockHeight : 0;

        const totalBlockHeight =
          titleBlockHeight + (priceHeightTinyPls > 0 ? gapTitlePrice : 0) + priceHeightTinyPls;

        const centerY = innerBottom + innerHeight / 2;
        let blockTopY = centerY + totalBlockHeight / 2 + BOTTOM_BIAS_PT;
        let blockBottomY = blockTopY - totalBlockHeight;
        const minBlockBottomY = innerBottom + bottomMarginReq;
        if (blockBottomY < minBlockBottomY) {
          const delta = minBlockBottomY - blockBottomY;
          blockTopY += delta;
          blockBottomY += delta;
        }

        let cursorY = blockTopY;

        if (titleBlockHeight > 0) {
          const titleTopY = cursorY;
          const titleBottomY = titleTopY - titleBlockHeight;
          drawTitleInBand(page, titleText, {
            font: boldFont,
            maxSize: titleMaxSizeTinyPls,
            minSize: titleMinSizeTinyPls,
            lineHeight: titleLineHeightTinyPls,
            maxWidth: fullInnerWidth,
            startX: innerLeft,
            startY: titleTopY,
            endY: titleBottomY,
            maxLines: titleRules.maxLines,
            rules: titleRules,
          });
          cursorY = titleBottomY - (priceHeightTinyPls > 0 ? gapTitlePrice : 0);
        }

        if (priceText && priceHeightTinyPls > 0) {
          const priceBaselineY = cursorY - priceHeightTinyPls;
          const labelBottomY = y - preset.h;
          const minPriceBaselineY = labelBottomY + bottomMarginReq + priceHeightTinyPls * 0.1;
          const finalPriceBaselineY = Math.max(priceBaselineY, minPriceBaselineY);
          const priceWidth = boldFont.widthOfTextAtSize(priceText, priceSizeTinyPls);
          const priceX = innerLeft + (fullInnerWidth - priceWidth) / 2;
          page.drawText(priceText, {
            x: priceX,
            y: finalPriceBaselineY,
            font: boldFont,
            size: priceSizeTinyPls,
            color: rgb(0, 0, 0),
          });
        }

      } else if (isTiny) {
        // --- 5167: Fully centered tiny layout (business name, title, price) ---
        const GAP_TOP_PADDING_5167 = 2;
        const GAP_NAME_TO_TITLE_5167 = 1;
        const GAP_TITLE_TO_PRICE_5167 = 1;
        const GAP_BOTTOM_PADDING_5167 = 2;

        const priceMaxTiny = 8;
        const priceMinTiny = 5;
        let priceSizeTiny = calculatePriceFontSize(priceText, boldFont, priceMaxTiny, priceMinTiny, fullInnerWidth);
        let priceHeightTiny = boldFont.heightAtSize(priceSizeTiny);

        const nameMaxSize = 7;
        const nameMinSize = 5;
        const nameSize = footerTextForItem
          ? calculatePriceFontSize(footerTextForItem, font, nameMaxSize, nameMinSize, fullInnerWidth)
          : 0;
        const nameHeight = footerTextForItem ? font.heightAtSize(nameSize) : 0;

        const nameTop = innerTop - GAP_TOP_PADDING_5167;
        const nameBottom = nameTop - nameHeight;

        const priceBottom = innerBottom + GAP_BOTTOM_PADDING_5167;
        const priceTop = priceBottom + priceHeightTiny;

        const titleAreaTop = nameBottom - GAP_NAME_TO_TITLE_5167;
        const titleAreaBottom = priceTop + GAP_TITLE_TO_PRICE_5167;

        // 1. Business name (tiny, centered)
        if (footerTextForItem) {
          const nameLineWidth = font.widthOfTextAtSize(footerTextForItem, nameSize);
          const nameX = innerLeft + (fullInnerWidth - nameLineWidth) / 2;
          page.drawText(footerTextForItem, {
            x: nameX,
            y: nameTop - nameHeight,
            font,
            size: nameSize,
            color: rgb(0.3, 0.3, 0.3),
          });
        }

        const titleText = rawTitleText;

        // 2. Title (middle band, centered, dynamic size, up to 2 lines, 5167-specific logic)
        let titleMaxSize = 8;
        const titleLenTiny = titleText.length;
        if (titleLenTiny > 24) titleMaxSize = 7;
        if (titleLenTiny > 40) titleMaxSize = 6;
        const titleMinSize = 6;
        const titleLineHeightTiny = 1.0;

        const titleRules = getTitleRules('AVERY_5167');
        drawTitleInBand(page, titleText, {
          font: boldFont,
          maxSize: titleMaxSize,
          minSize: titleMinSize,
          lineHeight: titleLineHeightTiny,
          maxWidth: fullInnerWidth,
          startX: innerLeft,
          startY: titleAreaTop,
          endY: titleAreaBottom,
          maxLines: titleRules.maxLines,
          rules: titleRules,
        });
        // 3. Price (bottom, centered, anchored)
        if (priceText) {
          const priceWidth = boldFont.widthOfTextAtSize(priceText, priceSizeTiny);
          const priceX = innerLeft + (fullInnerWidth - priceWidth) / 2;
          page.drawText(priceText, {
            x: priceX,
            y: priceBottom,
            font: boldFont,
            size: priceSizeTiny,
            color: rgb(0, 0, 0),
          });
        }

      } else if (is94102) {
        // --- 94102: Centered stack (title, price only) ---
        const innerHeight = innerTop - innerBottom;

        const gapTitlePrice = 1;

        const rawTitleSquare = applyAvery94102TitleCap(rawTitleText);
        const maxCharsSquare = 'Tarrega Ft-110 Classical Guitar'.length;
        const wordsSquare = rawTitleSquare.split(/\s+/).filter(Boolean);
        let titleText = '';
        for (const word of wordsSquare) {
          const candidate = titleText ? `${titleText} ${word}` : word;
          if (candidate.length > maxCharsSquare) break;
          titleText = candidate;
        }

        let titleMaxSizeSquare = 13;
        const titleLenSquare = titleText.length;
        if (titleLenSquare > 24) titleMaxSizeSquare = 11;
        if (titleLenSquare > 40) titleMaxSizeSquare = 9;
        const titleMinSizeSquare = 6;
        const titleLineHeightSquare = 1.0;

        const priceMaxSquare = 11;
        const priceMinSquare = 7;
        const priceSizeSquare = calculatePriceFontSize(priceText, boldFont, priceMaxSquare, priceMinSquare, fullInnerWidth);
        const priceHeightSquare = priceText ? boldFont.heightAtSize(priceSizeSquare) : 0;

        const titleRules = getTitleRules('AVERY_94102');
        const maxTitleAvailableHeight = Math.max(
          0,
          innerHeight - priceHeightSquare - (priceHeightSquare > 0 ? gapTitlePrice : 0)
        );
        const titleLayout = computeTitleLayout(
          titleText,
          boldFont,
          titleMaxSizeSquare,
          titleMinSizeSquare,
          titleLineHeightSquare,
          fullInnerWidth,
          titleRules.maxLines,
          titleRules,
          maxTitleAvailableHeight
        );
        const titleBlockHeight = titleLayout ? titleLayout.blockHeight : 0;

        const totalBlockHeight =
          titleBlockHeight + (priceHeightSquare > 0 ? gapTitlePrice : 0) + priceHeightSquare;

        const centerY = innerBottom + innerHeight / 2;
        let blockTopY = centerY + totalBlockHeight / 2 + BOTTOM_BIAS_PT;
        let blockBottomY = blockTopY - totalBlockHeight;
        const minBlockBottomY = innerBottom + bottomMarginReq;
        if (blockBottomY < minBlockBottomY) {
          const delta = minBlockBottomY - blockBottomY;
          blockTopY += delta;
          blockBottomY += delta;
        }

        let cursorY = blockTopY;

        if (titleBlockHeight > 0) {
          const titleTopY = cursorY;
          const titleBottomY = titleTopY - titleBlockHeight;
          drawTitleInBand(page, titleText, {
            font: boldFont,
            maxSize: titleMaxSizeSquare,
            minSize: titleMinSizeSquare,
            lineHeight: titleLineHeightSquare,
            maxWidth: fullInnerWidth,
            startX: innerLeft,
            startY: titleTopY,
            endY: titleBottomY,
            maxLines: titleRules.maxLines,
            rules: titleRules,
          });
          cursorY = titleBottomY - (priceHeightSquare > 0 ? gapTitlePrice : 0);
        }

        if (priceText && priceHeightSquare > 0) {
          const priceBaselineY = cursorY - priceHeightSquare;
          const labelBottomY = y - preset.h;
          const minPriceBaselineY = labelBottomY + bottomMarginReq + priceHeightSquare * 0.1;
          const finalPriceBaselineY = Math.max(priceBaselineY, minPriceBaselineY);
          const priceWidth = boldFont.widthOfTextAtSize(priceText, priceSizeSquare);
          const priceX = innerLeft + (fullInnerWidth - priceWidth) / 2;
          page.drawText(priceText, {
            x: priceX,
            y: finalPriceBaselineY,
            font: boldFont,
            size: priceSizeSquare,
            color: rgb(0, 0, 0),
          });
        }

      } else {
        // --- Fallback sheet logic (non-5160, non-5167, non-94102) ---
        const nameSize = 9;
        const nameY = innerTop - nameSize;
        if (footerTextForItem) {
          drawSingleLineEllipsis(page, footerTextForItem, {
            font,
            size: nameSize,
            maxWidth: fullInnerWidth,
            x: innerLeft,
            y: nameY,
            color: rgb(0.3, 0.3, 0.3),
          });
        }

        const priceMax = 32;
        const priceMin = 10;
        const priceZoneWidth = fullInnerWidth;
        let priceSize = calculatePriceFontSize(priceText, boldFont, priceMax, priceMin, priceZoneWidth);
        let priceHeight = boldFont.heightAtSize(priceSize);
        const priceY = innerBottom;

        if (priceText) {
          const priceWidth = boldFont.widthOfTextAtSize(priceText, priceSize);
          const priceX = innerRight - priceWidth;
          page.drawText(priceText, {
            x: priceX,
            y: priceY,
            font: boldFont,
            size: priceSize,
            color: rgb(0, 0, 0),
          });
        }
        const priceTotalHeight = priceText ? priceHeight + lawGap : 0;

        const titleMaxSize = 20;
        const titleMinSize = 12;
        const titleLineHeight = 1.05;

        const nameTotalHeight = footerTextForItem ? nameSize + lawGap : 0;
        const titleAreaTop = innerTop - nameTotalHeight;
        const titleAreaBottom = innerBottom + priceTotalHeight;

        const titleRules = getTitleRules('DEFAULT');
        drawTitleInBand(page, rawTitleText, {
          font: boldFont,
          maxSize: titleMaxSize,
          minSize: titleMinSize,
          lineHeight: titleLineHeight,
          maxWidth: fullInnerWidth,
          startX: innerLeft,
          startY: titleAreaTop,
          endY: titleAreaBottom,
          maxLines: titleRules.maxLines,
          rules: titleRules,
        });
      }

      count++;
    }
  }

  return pdfDoc;
};

const exportBrotherCsv = (items, fileStem, currentSaleName, businessName) => {
  // --- Original CSV function (UNCHANGED) ---
  const expandedItems = [];
  for (const item of items) {
    const count = toIntOr(item?.quantity_estimate, 1);
    for (let i = 0; i < count; i++) expandedItems.push(item);
  }

  const csvQuote = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const headers = ['Filename', 'Section', 'Title', 'Description', 'Price', 'SaleName', 'BusinessName'];

  const dataLines = expandedItems.map((item) => {
    const line = [
      item.filename || '',
      item.section_name || item.section || '',
      item.title || '',
      item.description || '',
      formatUSD(item.per_item_cost_estimate),
      currentSaleName,
      businessName,
    ];
    return line.map(csvQuote).join(',');
  });

  const content = [headers.join(','), ...dataLines].join('\r\n');
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${fileStem}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
  return expandedItems.length.toLocaleString();
};

// Total number of physical labels that will print (sum of quantity_estimate across items).
const countPrintLabels = (items) => {
  let total = 0;
  for (const item of items || []) {
    total += toIntOr(item?.quantity_estimate, 1);
  }
  return total;
};
// --- Helper Functions (UNCHANGED) ---
const formatSaleDateNoYear = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d);
};

const getSectionName = async (sectionId, sections) => {
  if (sectionId === 'all') return 'all sections';
  return sections.find(s => s.id === sectionId)?.name || 'all sections';
};

const slug = (s) => String(s || '').normalize('NFKD').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-').toLowerCase();

const firstTwoWords = (s) => String(s || '').trim().split(/\s+/).slice(0, 2).join(' ');

const LabelPrinter = {
  async generate({ items, presetKey, businessName, saleName, sectionId, onStatus, currentSale, selectedSectionId, sections, selectedSaleId, fetchWrapper, clientId, fetchInventory, setStatus, toIntOr, enableSpacers = true, startAt = 1, slug = slug, firstTwoWords = firstTwoWords, formatSaleDateNoYear = formatSaleDateNoYear, getSectionName = getSectionName }) {
    // --- Original Generate function (UNCHANGED, TYPOS FIXED) ---
    const preset = PRESET_REGISTRY[presetKey];
    if (!preset) throw new Error(`Invalid preset: ${presetKey}`);
    const itemsToProcess = items.slice(); // preserve incoming order; do not re-sort

    const normalizeSectionName = (value) =>
      String(value || '').trim() || 'UNASSIGNED';
    const hasMultipleSections = (() => {
      const set = new Set();
      for (const it of itemsToProcess) {
        set.add(normalizeSectionName(it?.section_name || it?.sectionName));
        if (set.size > 1) return true;
      }
      return false;
    })();
    const shouldInsertSpacers =
      enableSpacers && selectedSectionId === 'all' && hasMultipleSections;

    const itemsWithSpacers = (() => {
      if (!shouldInsertSpacers) return itemsToProcess.slice();
      const next = [];
      let prevSection = null;
      for (const it of itemsToProcess) {
        const sectionName = normalizeSectionName(it?.section_name || it?.sectionName);
        if (prevSection !== null && sectionName !== prevSection) {
          next.push({
            id: `spacer-${sectionName}-${next.length}`,
            isSpacer: true,
            section_name: sectionName,
            title: `Next Tags: ${sectionName}`,
            quantity_estimate: 1,
          });
        }
        next.push(it);
        prevSection = sectionName;
      }
      return next;
    })();

    const labelCount = countPrintLabels(itemsWithSpacers);
    const itemCount = itemsToProcess.length;

    // Immediately surface the print quantity at click-time (before PDF/CSV generation).
    const preMsg = `Printing ${labelCount.toLocaleString()} tag${labelCount === 1 ? '' : 's'} from ${itemCount.toLocaleString()} item${itemCount === 1 ? '' : 's'}…`;
    onStatus?.(preMsg);
    setStatus?.(preMsg);

    let fileName, successMessage;

    const buildFileName = async (ext) => {
      const now = new Date();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      let hour = now.getHours();
      const isPm = hour >= 12;
      hour = hour % 12;
      if (hour === 0) hour = 12;
      const min = String(now.getMinutes()).padStart(2, '0');
      const time = `${hour}${min}${isPm ? 'p' : 'a'}`;
      const date = `${mm}-${dd}`;

      const cleanSlug = (value) =>
        String(value || '')
          .replace(/\s+/g, '')
          .replace(/[\/\\:*?"<>|]/g, '-')
          .replace(/[^A-Za-z0-9-]/g, '-');

      const saleSlug = cleanSlug(saleName) || 'Sale';
      let sectionLabel = 'ALL';
      if (selectedSectionId !== 'all') {
        sectionLabel = await getSectionName(sectionId, sections);
        if (!sectionLabel) sectionLabel = 'UNASSIGNED';
      }
      const sectionSlug = cleanSlug(sectionLabel) || 'UNASSIGNED';

      return `${saleSlug}.${sectionSlug}.${date}.${time}.${ext}`;
    };

    const firstRealItem = itemsToProcess.find((it) => !it?.isSpacer) || null;
    const firstSectionName = normalizeSectionName(firstRealItem?.section_name ?? firstRealItem?.sectionName);
    let headingText = '';
    if (selectedSectionId === 'all') {
      headingText = `ALL SECTIONS - STARTS WITH: ${firstSectionName || 'UNASSIGNED'}`;
    } else {
      let sectionName = firstSectionName;
      if (!sectionName || sectionName === 'UNASSIGNED') {
        try {
          const resolved = await getSectionName(selectedSectionId, sections);
          if (resolved) sectionName = resolved;
        } catch {
        }
      }
      headingText = `SECTION: ${sectionName || 'UNASSIGNED'}`;
    }

    if (preset.kind === 'csv') {
      const fileStem = await buildFileName('csv');
      const expandedCount = exportBrotherCsv(itemsToProcess, fileStem.replace(/\.csv$/, ''), saleName, businessName);
      successMessage = `Exported CSV for ${itemsToProcess.length} items (${expandedCount} tags).`;
      fileName = fileStem;
    } else {
      let pdfDoc;
      if (preset.kind === 'thermal') {
        pdfDoc = await generateThermalPdf(preset, itemsWithSpacers, businessName, '');
      } else if (preset.kind === 'sheet') {
        pdfDoc = await generateAverySheetPdf(preset, itemsWithSpacers, businessName, headingText, startAt);
      }
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      fileName = await buildFileName('pdf');
      a.download = fileName;
      a.click();
      if (presetKey === 'PHOMEMO_40x30' || presetKey === 'PHOMEMO_30x20') {
        window.open(url, '_blank');
      }
      URL.revokeObjectURL(url);
      successMessage = `Downloaded ${fileName} for ${itemsToProcess.length} items.`;
    }

    onStatus?.(successMessage);
    setStatus?.(successMessage);

    // Tag items
    if (itemsToProcess.length > 0) {
      try {
        const itemIds = itemsToProcess
          .filter((item) => !item?.isSpacer)
          .map((item) => item.id);
        await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/update_inventory_items_tagged`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ p_ids: itemIds, p_sale_id: selectedSaleId, p_client_id: clientId }),
        });
        setStatus(`Tagged ${itemsToProcess.length} items as printed.`);
        await fetchInventory();
      } catch (err) {
        console.error('Error tagging items:', err);
      }
    }

    return { fileName, successMessage, itemCount, labelCount };
  },
};

export default LabelPrinter;

